import React, { useMemo, useCallback } from 'react';
import Select, { SingleValue, GroupBase } from 'react-select';
import { useTranslation } from 'react-i18next';
import { UNIT_CODES, getUnitsGroupedByCategory, getUnitByCode, UnitCode } from '../../utils/unitCodeMapping';
import { reactSelectTokenStyles } from '../../styles/reactSelectTokenStyles';

interface UnitOption {
  value: string;
  label: string;
  symbol?: string;
}

interface UnitGroup {
  label: string;
  options: UnitOption[];
}

interface UnitCodeSelectorProps {
  value: string | null;
  onChange: (code: string | null) => void;
  placeholder?: string;
  className?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
}

const UnitCodeSelector: React.FC<UnitCodeSelectorProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  isDisabled = false,
  isClearable = true,
  menuPlacement = 'auto'
}) => {
  const { t, i18n } = useTranslation('common');

  // Create grouped options for the select
  const groupedOptions = useMemo((): UnitGroup[] => {
    const grouped = getUnitsGroupedByCategory();
    
    return Object.entries(grouped).map(([category, units]) => ({
      label: t(`units.categories.${category}`),
      options: units.map(unit => ({
        value: unit.code,
        label: `${t(unit.nameKey)}${unit.symbol ? ` (${unit.symbol})` : ''}`,
        symbol: unit.symbol
      }))
    }));
  }, [t, i18n.language]);

  // Get the current selected option
  const selectedOption = useMemo((): UnitOption | null => {
    if (!value) return null;
    
    const unit = getUnitByCode(value);
    if (!unit) {
      // If code is not in our list, show the raw code
      return {
        value: value,
        label: value
      };
    }
    
    return {
      value: unit.code,
      label: `${t(unit.nameKey)}${unit.symbol ? ` (${unit.symbol})` : ''}`,
      symbol: unit.symbol
    };
  }, [value, t, i18n.language]);

  // Handle selection change
  const handleChange = useCallback((option: SingleValue<UnitOption>) => {
    onChange(option ? option.value : null);
  }, [onChange]);

  return (
    <Select<UnitOption, false, GroupBase<UnitOption>>
      className={`react-select-container ${className}`}
      classNamePrefix="react-select"
      value={selectedOption}
      onChange={handleChange}
      options={groupedOptions}
      placeholder={placeholder || t('positions.unitPlaceholder') || 'Select unit...'}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isSearchable={true}
      menuPlacement={menuPlacement}
      styles={reactSelectTokenStyles}
      noOptionsMessage={() => t('common.noOptionsFound', { defaultValue: 'No options found' })}
    />
  );
};

export default UnitCodeSelector;

// Helper component to display unit name from code (read-only display)
export const UnitCodeDisplay: React.FC<{ code: string | null | undefined }> = ({ code }) => {
  const { t } = useTranslation('common');
  
  if (!code) return <span className="text-faint">-</span>;
  
  const unit = getUnitByCode(code);
  if (!unit) return <span className="text-secondary">{code}</span>;
  
  return (
    <span className="text-secondary">
      {t(unit.nameKey)}
      {unit.symbol && <span className="text-faint ml-1">({unit.symbol})</span>}
    </span>
  );
};
